/**
 * Process Data Tab Component
 *
 * Displays metadata about the process and deadline (prazo).
 */

import { useState } from "react";
import { Button, Spinner } from "@fluentui/react-components";
import {
  ArrowUpload24Regular,
  Checkmark24Regular,
  Open24Regular,
} from "@fluentui/react-icons";

import { Documento, DocumentoStatus } from "../../../types";
import { oliDocClient } from "../../../services";

interface Props {
  documento: Documento;
  onStatusChange: (documento: Documento) => void;
}

export function ProcessDataTab({ documento, onStatusChange }: Props) {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleStatusChange(newStatus: DocumentoStatus) {
    setUpdating(true);
    setError(null);

    try {
      const updated = await oliDocClient.updateStatus(documento.id, newStatus);
      onStatusChange(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao atualizar status");
    } finally {
      setUpdating(false);
    }
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Data inválida";
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div className="process-data-tab">
      {/* Process Info */}
      <section className="section">
        <div className="section-title">Processo</div>
        <div className="info-list">
          <div className="info-item">
            <span className="info-label">ID</span>
            <span className="info-value">{documento.processo_sf_id}</span>
          </div>
          {documento.prazo_sf_id && (
            <div className="info-item">
              <span className="info-label">Prazo</span>
              <span className="info-value">{documento.prazo_sf_id}</span>
            </div>
          )}
          {documento.cliente_sf_id && (
            <div className="info-item">
              <span className="info-label">Cliente</span>
              <span className="info-value">{documento.cliente_sf_id}</span>
            </div>
          )}
        </div>
      </section>

      {/* Document Info */}
      <section className="section">
        <div className="section-title">Documento</div>
        <div className="info-list">
          <div className="info-item">
            <span className="info-label">Nome</span>
            <span className="info-value">{documento.file_name}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Status</span>
            <span className="info-value">
              <StatusBadge status={documento.status} />
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Tipo</span>
            <span className="info-value">{documento.doc_tipo || "Não definido"}</span>
          </div>
          {documento.template_id && (
            <div className="info-item">
              <span className="info-label">Template</span>
              <span className="info-value">{documento.template_id}</span>
            </div>
          )}
          <div className="info-item">
            <span className="info-label">Criado em</span>
            <span className="info-value">{formatDate(documento.created_at)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Atualizado em</span>
            <span className="info-value">{formatDate(documento.updated_at)}</span>
          </div>
        </div>
      </section>

      {/* Error */}
      {error && (
        <div className="error-box">
          <p>{error}</p>
        </div>
      )}

      {/* Actions */}
      <section className="section">
        <div className="section-title">Ações</div>
        <div className="actions">
          {documento.status === "ELABORACAO" && (
            <Button
              appearance="primary"
              icon={<ArrowUpload24Regular />}
              onClick={() => handleStatusChange("REVISAO")}
              disabled={updating}
            >
              {updating ? <Spinner size="tiny" /> : "Enviar para Revisão"}
            </Button>
          )}
          {documento.status === "REVISAO" && (
            <div className="actions-row">
              <Button
                appearance="secondary"
                onClick={() => handleStatusChange("ELABORACAO")}
                disabled={updating}
              >
                Voltar para Elaboração
              </Button>
              <Button
                appearance="primary"
                icon={<Checkmark24Regular />}
                onClick={() => handleStatusChange("CONCLUIDO")}
                disabled={updating}
              >
                {updating ? <Spinner size="tiny" /> : "Concluir"}
              </Button>
            </div>
          )}
          {documento.status === "CONCLUIDO" && (
            <Button
              appearance="secondary"
              onClick={() => handleStatusChange("REVISAO")}
              disabled={updating}
            >
              Reabrir para Revisão
            </Button>
          )}

          {documento.web_url && (
            <Button
              appearance="subtle"
              icon={<Open24Regular />}
              as="a"
              href={documento.web_url}
              target="_blank"
              rel="noreferrer noopener"
              style={{ marginTop: 8 }}
            >
              Abrir no SharePoint
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: DocumentoStatus }) {
  const labels: Record<DocumentoStatus, string> = {
    ELABORACAO: "Elaboração",
    REVISAO: "Revisão",
    CONCLUIDO: "Concluído",
  };

  return (
    <span className={`status-badge ${status.toLowerCase()}`}>
      {labels[status]}
    </span>
  );
}
